import { AUTO_LAUNCH_RULES, GAME_FRAME, LUCKY_SPARK_RULES } from "../../config/gameplay";
import { clamp, formatCompact } from "../../utils/math";
import { rngUnit } from "../../utils/rng";
import { ticketStorageCount } from "../auto-launch/catalog";
import type { ProfileProgress } from "../progression/levels";
import { applyCountedCredits, estimatedCreditsPerSecond, roundTimedReward } from "../rewards/credits";
import type { OrbitForgeSave } from "../save/types";
import type { FeedbackDraft } from "../runtime/feedback";

export interface LuckySparkRuntimeState {
  initialized: boolean;
  active: boolean;
  delaySeconds: number;
  remainingSeconds: number;
  frameX: number;
  frameY: number;
}

export interface LuckySparkView {
  readonly unlocked: boolean;
  readonly eligible: boolean;
  readonly active: boolean;
  readonly remainingSeconds: number;
  readonly durationSeconds: number;
  readonly frameX: number;
  readonly frameY: number;
  readonly progress: number;
}

export interface LuckySparkAdvanceResult {
  readonly dirty: boolean;
  readonly feedback: readonly FeedbackDraft[];
}

export interface LuckySparkClaimResult {
  readonly dirty: boolean;
  readonly reward: number;
  readonly ticketGranted: boolean;
  readonly feedback: readonly FeedbackDraft[];
}

interface Rect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

const sparkHalfSize = 58;

export function createLuckySparkRuntimeState(): LuckySparkRuntimeState {
  return {
    initialized: false,
    active: false,
    delaySeconds: 0,
    remainingSeconds: 0,
    frameX: 0,
    frameY: 0
  };
}

export function buildLuckySparkView(save: OrbitForgeSave, state: LuckySparkRuntimeState, progress: ProfileProgress): LuckySparkView {
  const eligible = isLuckySparkEligible(save, progress);
  const active = eligible && state.active;
  return {
    unlocked: progress.effectiveLevel >= LUCKY_SPARK_RULES.unlockLevel,
    eligible,
    active,
    remainingSeconds: active ? state.remainingSeconds : 0,
    durationSeconds: LUCKY_SPARK_RULES.activeDurationSeconds,
    frameX: active ? state.frameX : 0,
    frameY: active ? state.frameY : 0,
    progress: active ? clamp(state.remainingSeconds / LUCKY_SPARK_RULES.activeDurationSeconds, 0, 1) : 0
  };
}

export function advanceLuckySpark(input: {
  readonly save: OrbitForgeSave;
  readonly state: LuckySparkRuntimeState;
  readonly progress: ProfileProgress;
  readonly deltaSeconds: number;
  readonly activePanel: string | null;
  readonly modalBlocked: boolean;
}): LuckySparkAdvanceResult {
  if (!isLuckySparkEligible(input.save, input.progress)) {
    resetLuckySpark(input.state);
    return { dirty: false, feedback: [] };
  }

  if (input.modalBlocked) {
    if (input.state.active) {
      scheduleDelay(input.save, input.state, "missed");
      return { dirty: true, feedback: [] };
    }
    return { dirty: false, feedback: [] };
  }

  if (!input.state.initialized) {
    scheduleDelay(input.save, input.state, "initial");
    return { dirty: true, feedback: [] };
  }

  if (input.state.active) {
    input.state.remainingSeconds = Math.max(0, input.state.remainingSeconds - input.deltaSeconds);
    if (input.state.remainingSeconds <= 0) {
      scheduleDelay(input.save, input.state, "missed");
      return { dirty: true, feedback: [] };
    }
    return { dirty: false, feedback: [] };
  }

  input.state.delaySeconds = Math.max(0, input.state.delaySeconds - input.deltaSeconds);
  if (input.state.delaySeconds > 0) {
    return { dirty: false, feedback: [] };
  }

  const position = chooseLuckySparkPosition(input.save.rngState, input.activePanel);
  input.save.rngState = position.nextRngState;
  input.state.active = true;
  input.state.remainingSeconds = LUCKY_SPARK_RULES.activeDurationSeconds;
  input.state.frameX = position.frameX;
  input.state.frameY = position.frameY;
  return {
    dirty: true,
    feedback: [{ kind: "pulse", target: "lucky-spark" }]
  };
}

export function runClaimLuckySparkCommand(save: OrbitForgeSave, state: LuckySparkRuntimeState, progress: ProfileProgress): LuckySparkClaimResult {
  if (!state.active || !isLuckySparkEligible(save, progress)) {
    return {
      dirty: false,
      reward: 0,
      ticketGranted: false,
      feedback: [{ kind: "denied", target: "lucky-spark", frameX: state.frameX, frameY: state.frameY }]
    };
  }

  const reward = calculateLuckySparkReward(save);
  applyCountedCredits(save, reward);
  save.stats.LuckySparksClaimed += 1;

  const ticketRoll = rngUnit(save.rngState);
  save.rngState = ticketRoll.state;
  const ticketGranted = ticketRoll.value <= LUCKY_SPARK_RULES.ticketChance && ticketStorageCount(save) < AUTO_LAUNCH_RULES.storageCap;
  if (ticketGranted) {
    save.stats.AutoLaunchTickets60 += 1;
  }

  const frameX = state.frameX;
  const frameY = state.frameY;
  scheduleDelay(save, state, "claimed");

  return {
    dirty: true,
    reward,
    ticketGranted,
    feedback: [
      { kind: "spark-claim", target: "lucky-spark", frameX, frameY, color: "gold" },
      { kind: "value-gain", frameX, frameY: frameY - 48, color: "gold", text: `+${formatCompact(reward)}` },
      ...(ticketGranted ? [{ kind: "floating-text" as const, frameX, frameY: frameY - 82, color: "cyan" as const, text: "60 SEC TICKET" }] : [])
    ]
  };
}

export function calculateLuckySparkReward(save: OrbitForgeSave): number {
  const earned = save.stats.TotalEarnedCredits;
  const raw = 120 + estimatedCreditsPerSecond(save) * 18 * 0.12 + Math.sqrt(earned + 1) * 18;
  const capped = clamp(raw, 120, Math.max(500, earned * 0.025));
  return roundTimedReward(capped);
}

export function chooseLuckySparkPosition(seed: number, activePanel: string | null): { readonly frameX: number; readonly frameY: number; readonly nextRngState: number } {
  let state = seed;
  for (let attempt = 0; attempt < 12; attempt += 1) {
    const xRoll = rngUnit(state);
    const yRoll = rngUnit(xRoll.state);
    state = yRoll.state;
    const frameX = 260 + xRoll.value * (GAME_FRAME.width - 520);
    const frameY = 190 + yRoll.value * 610;
    if (!intersectsBlockedRegions(frameX, frameY, activePanel)) {
      return { frameX, frameY, nextRngState: state };
    }
  }

  const fallback = fallbackPosition(activePanel);
  return { ...fallback, nextRngState: state };
}

function scheduleDelay(save: OrbitForgeSave, state: LuckySparkRuntimeState, mode: "initial" | "missed" | "claimed"): void {
  const range = mode === "initial"
    ? [LUCKY_SPARK_RULES.initialDelayMinSeconds, LUCKY_SPARK_RULES.initialDelayMaxSeconds]
    : mode === "missed"
      ? [LUCKY_SPARK_RULES.missedDelayMinSeconds, LUCKY_SPARK_RULES.missedDelayMaxSeconds]
      : [LUCKY_SPARK_RULES.claimedDelayMinSeconds, LUCKY_SPARK_RULES.claimedDelayMaxSeconds];
  const roll = rngUnit(save.rngState);
  save.rngState = roll.state;
  state.initialized = true;
  state.active = false;
  state.delaySeconds = range[0] + (range[1] - range[0]) * roll.value;
  state.remainingSeconds = 0;
  state.frameX = 0;
  state.frameY = 0;
}

function isLuckySparkEligible(save: OrbitForgeSave, progress: ProfileProgress): boolean {
  return progress.effectiveLevel >= LUCKY_SPARK_RULES.unlockLevel && save.stats.TotalPlayTimeSeconds >= LUCKY_SPARK_RULES.minimumPlayTimeSeconds;
}

function resetLuckySpark(state: LuckySparkRuntimeState): void {
  state.initialized = false;
  state.active = false;
  state.delaySeconds = 0;
  state.remainingSeconds = 0;
  state.frameX = 0;
  state.frameY = 0;
}

function intersectsBlockedRegions(frameX: number, frameY: number, activePanel: string | null): boolean {
  const bounds = {
    left: frameX - sparkHalfSize,
    top: frameY - sparkHalfSize,
    right: frameX + sparkHalfSize,
    bottom: frameY + sparkHalfSize
  };
  return blockedRegions(activePanel).some((rect) => rectsOverlap(bounds, rect));
}

function blockedRegions(activePanel: string | null): readonly Rect[] {
  return [
    { left: 0, top: 0, right: 540, bottom: 154 },
    { left: 520, top: 0, right: 1400, bottom: 154 },
    { left: 1640, top: 0, right: GAME_FRAME.width, bottom: 126 },
    { left: 0, top: 144, right: 222, bottom: 674 },
    { left: 1698, top: 144, right: GAME_FRAME.width, bottom: 540 },
    { left: 238, top: 826, right: 1682, bottom: GAME_FRAME.height },
    ...panelBlockedRegions(activePanel)
  ];
}

function panelBlockedRegions(activePanel: string | null): readonly Rect[] {
  if (activePanel === "forge" || activePanel === "auto" || activePanel === "crates") {
    return [{ left: 120, top: 118, right: 842, bottom: 960 }];
  }
  if (activePanel === "profile") {
    return [{ left: 20, top: 110, right: 552, bottom: 560 }];
  }
  if (activePanel === "settings") {
    return [{ left: 1028, top: 118, right: 1848, bottom: 960 }];
  }
  return [];
}

function fallbackPosition(activePanel: string | null): { readonly frameX: number; readonly frameY: number } {
  if (activePanel === "settings") {
    return { frameX: 650, frameY: 505 };
  }
  if (activePanel) {
    return { frameX: 1280, frameY: 505 };
  }
  return { frameX: 960, frameY: 430 };
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}
